(() => {
  const cfg = window.__FREE_GIFT__;
  if (!cfg || !cfg.enabled) return;

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  const CART_JSON = `${root}cart.js`;
  const ADD_URL = `${root}cart/add.js`;
  const CHANGE_URL = `${root}cart/change.js`;
  const CHECKOUT_URL = `${root}checkout`;

  const qualifyingSet = new Set((cfg.qualifyingProductIds || []).map((x) => Number(x)));
  const triggerMode = String(cfg.triggerMode || "any").toLowerCase();
  const minQtyEach = Math.max(1, Number(cfg.minQtyEach || 2));
  const qtyMode = String(cfg.qtyMode || "fixed_1").toLowerCase();

  let isProcessing = false;
  let isCheckoutValidating = false;
  let cartUpdateInProgress = false;
  let debounceTimer = null;
  let waveTimers = [];
  let lastKnownHasTrigger = false;

  const debounce = (fn, wait = 120) => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(fn, wait);
  };

  const fetchCart = async () => {
    const res = await fetch(CART_JSON, {
      credentials: "same-origin",
      cache: "no-store",
    });

    if (!res.ok) throw new Error("Failed to fetch cart");
    return res.json();
  };

  const postJSON = async (url, body) => {
    const res = await fetch(url, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      const txt = await res.text().catch(() => "");
      throw new Error(`Cart request failed: ${res.status} ${txt}`);
    }

    return res.json().catch(() => ({}));
  };

  const getDrawer = () => document.querySelector("m-cart-drawer");

  const refreshMinimogCartUI = () => {
    const drawer = getDrawer();
    if (drawer && typeof drawer.onCartDrawerUpdate === "function") {
      drawer.onCartDrawerUpdate(true);
    }
  };

  const lockGiftQtyUI = () => {
    const gProdId = String(cfg.giftProductId);
    const candidates = document.querySelectorAll(
      `[data-product-id="${gProdId}"], [data-cart-item-product-id="${gProdId}"]`
    );

    candidates.forEach((rootEl) => {
      rootEl.classList.add("is-free-gift");

      rootEl.querySelectorAll('input[name="updates[]"], input[type="number"]').forEach((inp) => {
        inp.setAttribute("disabled", "disabled");
        inp.setAttribute("readonly", "readonly");
      });

      rootEl
        .querySelectorAll(
          'button[name="minus"], button[name="plus"], .m-quantity__button, .quantity__button, [data-qty-minus], [data-qty-plus]'
        )
        .forEach((btn) => {
          btn.setAttribute("disabled", "disabled");
          btn.setAttribute("aria-disabled", "true");
          btn.style.pointerEvents = "none";
        });
    });
  };

  // ===== GATILHOS =====
  const getTriggerQtyMap = (cart) => {
    const map = new Map();
    qualifyingSet.forEach((pid) => map.set(pid, 0));

    for (const item of cart.items || []) {
      const pid = Number(item.product_id);
      if (qualifyingSet.has(pid)) {
        map.set(pid, (map.get(pid) || 0) + Number(item.quantity || 0));
      }
    }

    return map;
  };

  const hasAnyTriggerProduct = (cart) => {
    const qtyMap = getTriggerQtyMap(cart);
    for (const qty of qtyMap.values()) {
      if (Number(qty || 0) > 0) return true;
    }
    return false;
  };

  const hasTriggerInDOM = () => {
    for (const productId of qualifyingSet) {
      if (
        document.querySelector(`[data-cart-item][data-product-id="${productId}"]`) ||
        document.querySelector(`[data-product-id="${productId}"]`)
      ) {
        return true;
      }
    }
    return false;
  };

  const getTriggerPacksMap = (cart) => {
    const qtyMap = getTriggerQtyMap(cart);
    const packsMap = new Map();

    for (const [pid, qty] of qtyMap.entries()) {
      packsMap.set(pid, Math.floor(Number(qty || 0) / minQtyEach));
    }

    return packsMap;
  };

  const isQualified = (cart) => {
    const packsMap = getTriggerPacksMap(cart);

    if (triggerMode === "all") {
      if (packsMap.size === 0) return false;
      for (const packs of packsMap.values()) {
        if (packs < 1) return false;
      }
      return true;
    }

    for (const packs of packsMap.values()) {
      if (packs >= 1) return true;
    }

    return false;
  };

  const desiredGiftQty = (cart) => {
    if (!isQualified(cart)) return 0;

    if (qtyMode === "per_trigger") {
      const packsMap = getTriggerPacksMap(cart);
      let total = 0;

      for (const packs of packsMap.values()) {
        total += Number(packs || 0);
      }

      return Math.max(1, total);
    }

    return 1;
  };

  // ===== BRINDE =====
  const isGiftProductItem = (item) => Number(item.product_id) === Number(cfg.giftProductId);
  const isConfiguredGiftVariant = (item) => Number(item.variant_id) === Number(cfg.giftVariantId);

  const getGiftItems = (cart) =>
    (cart.items || []).filter((item) => isGiftProductItem(item) || isConfiguredGiftVariant(item));

  const findConfiguredGiftItem = (cart) =>
    (cart.items || []).find((item) => isConfiguredGiftVariant(item));

  const ensureGift = async () => {
    await postJSON(ADD_URL, {
      items: [
        {
          id: Number(cfg.giftVariantId),
          quantity: 1,
          properties: { _free_gift: "true" },
        },
      ],
    });
  };

  const setGiftQty = async (giftItem, qty) => {
    await postJSON(CHANGE_URL, {
      id: giftItem.key,
      quantity: Number(qty),
    });
  };

  const normalizeGiftCart = async (initialCart = null) => {
    let cart = initialCart || (await fetchCart());
    let changed = false;
    const wantQty = desiredGiftQty(cart);

    lastKnownHasTrigger = hasAnyTriggerProduct(cart);

    const invalidGiftItems = getGiftItems(cart).filter((item) => !isConfiguredGiftVariant(item));

    for (const item of invalidGiftItems) {
      await setGiftQty(item, 0);
      changed = true;
    }

    if (invalidGiftItems.length > 0) {
      cart = await fetchCart();
      lastKnownHasTrigger = hasAnyTriggerProduct(cart);
    }

    let giftItem = findConfiguredGiftItem(cart);

    if (wantQty === 0) {
      if (giftItem) {
        await setGiftQty(giftItem, 0);
        changed = true;
        cart = await fetchCart();
        lastKnownHasTrigger = hasAnyTriggerProduct(cart);
      }

      return { cart, changed };
    }

    if (!giftItem) {
      await ensureGift();
      changed = true;
      cart = await fetchCart();
      lastKnownHasTrigger = hasAnyTriggerProduct(cart);
      giftItem = findConfiguredGiftItem(cart);
    }

    if (giftItem && Number(giftItem.quantity || 0) !== wantQty) {
      await setGiftQty(giftItem, wantQty);
      changed = true;
      cart = await fetchCart();
      lastKnownHasTrigger = hasAnyTriggerProduct(cart);
    }

    return { cart, changed };
  };

  const isGiftCartValid = (cart) => {
    const wantQty = desiredGiftQty(cart);
    const giftItems = getGiftItems(cart);
    const configuredItems = giftItems.filter((item) => isConfiguredGiftVariant(item));
    const invalidItems = giftItems.filter((item) => !isConfiguredGiftVariant(item));

    if (invalidItems.length > 0) return false;

    if (wantQty === 0) {
      return configuredItems.length === 0;
    }

    if (configuredItems.length !== 1) return false;
    return Number(configuredItems[0].quantity || 0) === wantQty;
  };

  // ===== ESTADO DO CHECKOUT =====
  const getCheckoutButtons = () =>
    Array.from(document.querySelectorAll('button[name="checkout"], input[name="checkout"]'));

  const saveCheckoutButtonState = (button) => {
    if (!button || button.dataset.freeGiftStateSaved === "true") return;

    button.dataset.freeGiftStateSaved = "true";
    button.dataset.freeGiftOriginalHtml = button.innerHTML;
    button.dataset.freeGiftWasDisabled = button.disabled ? "true" : "false";
  };

  const setCheckoutCheckingState = (button, checking, showText = true) => {
    if (!button) return;

    if (checking) {
      saveCheckoutButtonState(button);
      button.disabled = true;
      button.setAttribute("aria-disabled", "true");
      button.classList.add("is-free-gift-checking");

      if (showText) {
        const textNode = button.querySelector("span:not(.m-spinner-icon)");
        if (textNode) {
          textNode.textContent = "Conferindo brinde...";
        } else if (button.tagName === "INPUT") {
          button.value = "Conferindo brinde...";
        } else {
          button.textContent = "Conferindo brinde...";
        }
      }
      return;
    }

    button.classList.remove("is-free-gift-checking");

    if (button.dataset.freeGiftWasDisabled !== "true") {
      button.disabled = false;
      button.removeAttribute("aria-disabled");
    }

    if (button.dataset.freeGiftOriginalHtml) {
      button.innerHTML = button.dataset.freeGiftOriginalHtml;
    }

    delete button.dataset.freeGiftStateSaved;
    delete button.dataset.freeGiftOriginalHtml;
    delete button.dataset.freeGiftWasDisabled;
  };

  const setAllCheckoutChecking = (checking) => {
    getCheckoutButtons().forEach((button) => setCheckoutCheckingState(button, checking, true));
  };

  const shouldShowChecking = () => lastKnownHasTrigger || hasTriggerInDOM();

  const beginCartChecking = () => {
    if (shouldShowChecking()) {
      setAllCheckoutChecking(true);
    }
  };

  const endCartChecking = () => {
    if (isCheckoutValidating || isProcessing || cartUpdateInProgress) return;
    setAllCheckoutChecking(false);
  };

  const syncFreeGift = async ({ forceChecking = false } = {}) => {
    if (isCheckoutValidating || isProcessing) return;

    isProcessing = true;

    try {
      const cart = await fetchCart();
      lastKnownHasTrigger = hasAnyTriggerProduct(cart);

      if (lastKnownHasTrigger || forceChecking) {
        setAllCheckoutChecking(true);
      }

      const result = await normalizeGiftCart(cart);

      if (result.changed) {
        refreshMinimogCartUI();
        setTimeout(lockGiftQtyUI, 180);
      } else {
        lockGiftQtyUI();
      }
    } catch (e) {
      console.error("[FreeGift] Sync error:", e);
    } finally {
      isProcessing = false;
      endCartChecking();
    }
  };

  const clearWaves = () => {
    waveTimers.forEach((timer) => clearTimeout(timer));
    waveTimers = [];
  };

  // ===== CHECKOUT GUARD =====
  const findCheckoutButton = (event) => {
    if (event.submitter && event.submitter.matches('[name="checkout"]')) {
      return event.submitter;
    }

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return null;
    return form.querySelector('button[name="checkout"], input[name="checkout"]');
  };

  const validateAndGoToCheckout = async (checkoutButton) => {
    if (isCheckoutValidating) return;

    isCheckoutValidating = true;
    clearWaves();
    setCheckoutCheckingState(checkoutButton, true, false);

    try {
      let cart = await fetchCart();
      const hasTrigger = hasAnyTriggerProduct(cart);
      const hasGift = getGiftItems(cart).length > 0;
      lastKnownHasTrigger = hasTrigger;

      if (!hasTrigger && !hasGift) {
        window.location.assign(CHECKOUT_URL);
        return;
      }

      if (hasTrigger) {
        setAllCheckoutChecking(true);
      }

      let result = await normalizeGiftCart(cart);
      cart = await fetchCart();
      lastKnownHasTrigger = hasAnyTriggerProduct(cart);

      if (!isGiftCartValid(cart)) {
        const retry = await normalizeGiftCart(cart);
        result.changed = result.changed || retry.changed;
        cart = await fetchCart();
        lastKnownHasTrigger = hasAnyTriggerProduct(cart);
      }

      if (!isGiftCartValid(cart)) {
        throw new Error("Não foi possível validar o brinde antes do checkout.");
      }

      if (result.changed) {
        refreshMinimogCartUI();
      }

      window.location.assign(CHECKOUT_URL);
    } catch (error) {
      console.error("[FreeGift] Checkout validation error:", error);
      setAllCheckoutChecking(false);

      const message = "Não foi possível conferir o brinde. Tente novamente.";

      if (
        window.MinimogTheme &&
        window.MinimogTheme.Notification &&
        typeof window.MinimogTheme.Notification.show === "function"
      ) {
        try {
          window.MinimogTheme.Notification.show({
            type: "warning",
            message,
          });
        } catch (_) {
          window.alert(message);
        }
      } else {
        window.alert(message);
      }
    } finally {
      isCheckoutValidating = false;
    }
  };

  document.addEventListener(
    "submit",
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      const checkoutButton = findCheckoutButton(event);
      if (!checkoutButton) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      validateAndGoToCheckout(checkoutButton);
    },
    true
  );

  // ===== SINCRONIZAÇÃO DURANTE ALTERAÇÕES DO CARRINHO =====
  document.addEventListener("cart:updating", () => {
    cartUpdateInProgress = true;
    beginCartChecking();
  });

  document.addEventListener("cart:updated", (event) => {
    const cart = event.detail && event.detail.cart;
    if (cart) {
      lastKnownHasTrigger = hasAnyTriggerProduct(cart);
      if (lastKnownHasTrigger) setAllCheckoutChecking(true);
    }

    debounce(() => syncFreeGift({ forceChecking: lastKnownHasTrigger }), 60);
  });

  document.addEventListener("cart:update-finished", () => {
    cartUpdateInProgress = false;
    debounce(() => syncFreeGift({ forceChecking: lastKnownHasTrigger }), 40);
  });

  const scheduleSyncWaves = ({ keepChecking = false } = {}) => {
    if (isCheckoutValidating) return;

    clearWaves();

    [300, 750, 1300].forEach((delay, index, arr) => {
      const timer = setTimeout(async () => {
        await syncFreeGift({ forceChecking: keepChecking });
        if (index === arr.length - 1) {
          endCartChecking();
        }
      }, delay);

      waveTimers.push(timer);
    });
  };

  document.addEventListener("DOMContentLoaded", async () => {
    try {
      const cart = await fetchCart();
      lastKnownHasTrigger = hasAnyTriggerProduct(cart);
    } catch (_) {}

    syncFreeGift({ forceChecking: lastKnownHasTrigger });
  });

  // Add-to-cart da PDP / Quick View.
  document.addEventListener(
    "submit",
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (findCheckoutButton(event)) return;

      const isATCForm =
        form.matches('[data-type="add-to-cart-form"]') ||
        !!form.closest("product-form");

      if (!isATCForm) return;

      const productForm = form.closest("product-form");
      const formProductId = Number(
        form.dataset.productId ||
        (productForm && productForm.dataset.productId) ||
        0
      );

      const addingTrigger = qualifyingSet.has(formProductId);
      const keepChecking = addingTrigger || lastKnownHasTrigger || hasTriggerInDOM();

      if (keepChecking) {
        setAllCheckoutChecking(true);
      }

      scheduleSyncWaves({ keepChecking });
    },
    true
  );

  try {
    if (
      window.MinimogEvents &&
      window.MinimogTheme &&
      window.MinimogTheme.pubSubEvents?.openCartDrawer
    ) {
      window.MinimogEvents.subscribe(window.MinimogTheme.pubSubEvents.openCartDrawer, () => {
        debounce(() => syncFreeGift({ forceChecking: lastKnownHasTrigger }), 80);
      });
    }
  } catch (_) {}

  window.addEventListener("pageshow", () => {
    debounce(() => syncFreeGift({ forceChecking: lastKnownHasTrigger }), 80);
  });
})();
